import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const BlindStructureManager = () => {
  const [structures, setStructures] = useState([]);
  const [selectedStructure, setSelectedStructure] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [structureName, setStructureName] = useState('');
  const [structureLevels, setStructureLevels] = useState([]);
  const [isTemplate, setIsTemplate] = useState(false);
  const [defaultDuration, setDefaultDuration] = useState('20');
  const [useDefaultDuration, setUseDefaultDuration] = useState(true);

  const getApiUrl = () => {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'http://localhost:3000';
    } else if (hostname === '10.0.0.28') {
      return `https://${hostname}/api`;
    } else {
      return `https://${hostname}/api`;
    }
  };

  useEffect(() => {
    loadStructures();
  }, []);

  const loadStructures = async () => {
    try {
      const response = await fetch(`${getApiUrl()}/blind-structures`);
      const data = await response.json();
      setStructures(data);
    } catch (error) {
      console.error('Error loading structures:', error);
    }
  };

  const createNewStructure = () => {
    setSelectedStructure(null);
    setIsEditing(true);
    setStructureName('');
    setIsTemplate(false);
    setStructureLevels([
      { level: 1, small_blind: 25, big_blind: 50, ante: 0, duration: 20, type: 'level' },
      { level: 2, small_blind: 50, big_blind: 100, ante: 0, duration: 20, type: 'level' },
      { level: 3, small_blind: 75, big_blind: 150, ante: 0, duration: 20, type: 'level' },
    ]);
  };

  const editStructure = (structure) => {
    setSelectedStructure(structure);
    setIsEditing(true);
    setStructureName(structure.name);
    setIsTemplate(structure.is_template);
    setStructureLevels(structure.structure_json);
  };

  const copyStructure = async (structureId) => {
    try {
      const newName = prompt('Enter name for copied structure:');
      if (!newName) return;

      const response = await fetch(`${getApiUrl()}/blind-structures/${structureId}/copy`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({ new_name: newName })
      });

      if (response.ok) {
        loadStructures();
      }
    } catch (error) {
      console.error('Error copying structure:', error);
    }
  };

  const saveStructure = async () => {
    try {
      const structureData = {
        name: structureName,
        structure_json: structureLevels,
        is_template: isTemplate
      };

      let response;
      if (selectedStructure) {
        // Update existing
        response = await fetch(`${getApiUrl()}/blind-structures/${selectedStructure.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify(structureData)
        });
      } else {
        // Create new
        response = await fetch(`${getApiUrl()}/blind-structures`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify(structureData)
        });
      }

      if (response.ok) {
        setIsEditing(false);
        loadStructures();
      }
    } catch (error) {
      console.error('Error saving structure:', error);
    }
  };

  const addLevel = () => {
    const newLevel = {
      level: structureLevels.length + 1,
      small_blind: 100,
      big_blind: 200,
      ante: 0,
      duration: useDefaultDuration ? (parseInt(defaultDuration) || 0) : 0,
      type: 'level'
    };
    setStructureLevels([...structureLevels, newLevel]);
  };

  const addBreak = () => {
    const newBreak = {
      level: structureLevels.length + 1,
      small_blind: 0,
      big_blind: 0,
      ante: 0,
      duration: 15,
      type: 'break'
    };
    setStructureLevels([...structureLevels, newBreak]);
  };

  const updateLevel = (index, field, value) => {
    const updated = [...structureLevels];
    updated[index] = { ...updated[index], [field]: value };
    setStructureLevels(updated);
  };

  const removeLevel = (index) => {
    const updated = structureLevels.filter((_, i) => i !== index);
    // Renumber levels
    updated.forEach((level, i) => {
      level.level = i + 1;
    });
    setStructureLevels(updated);
  };

  // NEW: Handle drag and drop reordering
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(structureLevels);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Renumber levels after reordering
    items.forEach((level, index) => {
      level.level = index + 1;
    });

    setStructureLevels(items);
  };

  if (isEditing) {
    return (
      <div className="container">
        <h1>Blind Structure Editor</h1>
        
        <div className="card">
          <h3>{selectedStructure ? 'Edit Structure' : 'Create New Structure'}</h3>
          
          <div className="form-group">
            <label>Structure Name:</label>
            <input 
              type="text"
              className="form-control"
              value={structureName}
              onChange={(e) => setStructureName(e.target.value)}
              placeholder="e.g., Standard Deepstack"
            />
          </div>

          <div className="form-group">
            <label>
              <input 
                type="checkbox"
                checked={isTemplate}
                onChange={(e) => setIsTemplate(e.target.checked)}
              />
              {' '}Save as Template (reusable)
            </label>
          </div>

	  <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '5px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0 }}>
              <input
                type="checkbox"
                checked={useDefaultDuration}
                onChange={(e) => setUseDefaultDuration(e.target.checked)}
              />
              Use default duration for new levels
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: 0 }}>
              Default (minutes):
              <input
                type="number"
                min="1"
                value={defaultDuration}
                onChange={(e) => setDefaultDuration(e.target.value)}
                disabled={!useDefaultDuration}
                style={{ width: '70px' }}
                className="form-control"
              />
            </label>
          </div>

          <h4>Tournament Levels <span style={{ fontSize: '14px', color: '#666' }}>(Drag to reorder)</span></h4>
          <div>
            <DragDropContext onDragEnd={handleDragEnd}>
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: '30px' }}>⋮⋮</th>
                    <th>Level</th>
                    <th>Type</th>
                    <th>Small Blind</th>
                    <th>Big Blind</th>
                    <th>Ante</th>
                    <th>Duration (min)</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <Droppable droppableId="levels">
                  {(provided) => (
                    <tbody {...provided.droppableProps} ref={provided.innerRef}>
                      {structureLevels.map((level, index) => (
                        <Draggable 
                          key={`level-${index}`} 
                          draggableId={`level-${index}`} 
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <tr
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              style={{
                                backgroundColor: level.type === 'break' ? '#fff3cd' : 'white',
                                ...provided.draggableProps.style,
                                opacity: snapshot.isDragging ? 0.8 : 1,
                                boxShadow: snapshot.isDragging ? '0 5px 10px rgba(0,0,0,0.3)' : 'none'
                              }}
                            >
                              <td {...provided.dragHandleProps} style={{ cursor: 'grab', textAlign: 'center', fontSize: '18px', color: '#999' }}>
                                ⋮⋮
                              </td>
                              <td>{level.level}</td>
                              <td>
                                <select 
                                  value={level.type}
                                  onChange={(e) => updateLevel(index, 'type', e.target.value)}
                                  className="form-control"
                                >
                                  <option value="level">Level</option>
                                  <option value="break">Break</option>
                                </select>
                              </td>
                              <td>
                                <input 
                                  type="number"
                                  value={level.small_blind}
                                  onChange={(e) => updateLevel(index, 'small_blind', parseInt(e.target.value) || 0)}
                                  className="form-control"
                                  disabled={level.type === 'break'}
                                />
                              </td>
                              <td>
                                <input 
                                  type="number"
                                  value={level.big_blind}
                                  onChange={(e) => updateLevel(index, 'big_blind', parseInt(e.target.value) || 0)}
                                  className="form-control"
                                  disabled={level.type === 'break'}
                                />
                              </td>
                              <td>
                                <input 
                                  type="number"
                                  value={level.ante}
                                  onChange={(e) => updateLevel(index, 'ante', parseInt(e.target.value) || 0)}
                                  className="form-control"
                                  disabled={level.type === 'break'}
                                />
                              </td>
                              <td>
                                <input 
                                  type="number"
                                  value={level.duration}
                                  onChange={(e) => updateLevel(index, 'duration', parseInt(e.target.value) || 20)}
                                  className="form-control"
                                />
                              </td>
                              <td>
                                <button 
                                  className="btn btn-danger"
                                  onClick={() => removeLevel(index)}
                                  style={{ fontSize: '12px', padding: '5px 10px' }}
                                >
                                  Remove
                                </button>
                              </td>
                            </tr>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </tbody>
                  )}
                </Droppable>
              </table>
            </DragDropContext>
          </div>

          <div style={{ margin: '20px 0' }}>
            <button className="btn" onClick={addLevel}>Add Level</button>
            <button className="btn" onClick={addBreak}>Add Break</button>
          </div>

          <div>
            <button className="btn btn-success" onClick={saveStructure}>
              {selectedStructure ? 'Update Structure' : 'Create Structure'}
            </button>
            <button className="btn" onClick={() => setIsEditing(false)}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <h1>Blind Structure Management</h1>
      
      <div className="card">
        <h3>Tournament Structures</h3>
        <button className="btn btn-success" onClick={createNewStructure}>
          Create New Structure
        </button>
        
        <div style={{ marginTop: '20px' }}>
          {structures.map(structure => (
            <div key={structure.id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h4>{structure.name}</h4>
                  <p style={{ color: '#666', margin: '5px 0' }}>
                    {structure.is_template ? '📋 Template' : '📄 Custom'} | 
                    {' '}{structure.structure_json.length} levels | 
                    {' '}Created: {new Date(structure.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <button className="btn" onClick={() => editStructure(structure)}>Edit</button>
                  <button className="btn" onClick={() => copyStructure(structure.id)}>Copy</button>
                </div>
              </div>
              
              <div style={{ marginTop: '10px', fontSize: '14px' }}>
                <strong>Preview:</strong> 
                {structure.structure_json.slice(0, 3).map(level => 
                  level.type === 'break' ? ` Break(${level.duration}m)` : ` ${level.small_blind}/${level.big_blind}(${level.duration}m)`
                ).join(' → ')}
                {structure.structure_json.length > 3 && '...'}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BlindStructureManager;
